import { types as sdkTypes } from './sdkLoader';

const { UUID } = sdkTypes;

/**
 * Deal conversation messages come from Parnexus's own store (server/api/deals)
 * instead of Sharetribe. These helpers give them the shape the chat already
 * renders (Sharetribe's message entity: id, attributes, sender), so the chat
 * itself doesn't change.
 *
 * REACT_APP_DEAL_MESSAGES=off goes back to reading and sending through
 * Sharetribe directly.
 */
export const dealMessagesEnabled = () => process.env.REACT_APP_DEAL_MESSAGES !== 'off';

/** The sender as a user entity, from the display names the server sends. Pure. */
const senderEntity = user => ({
  id: new UUID(user?.id || 'unknown'),
  type: 'user',
  attributes: {
    banned: !!user?.banned,
    deleted: !!user?.deleted,
    profile: {
      displayName: user?.displayName || null,
      abbreviatedName: user?.abbreviatedName || null,
    },
  },
});

/** One message from the store as a message entity. Pure. */
export const toMessageEntity = message => ({
  id: new UUID(message.id),
  type: 'message',
  attributes: {
    content: message.content,
    createdAt: new Date(message.createdAt),
  },
  sender: senderEntity(message.sender?.user),
});

/**
 * Messages with each sender swapped for the transaction's own customer or
 * provider entity, which carries the profile image. Messages and the
 * transaction load at the same time, so this is done when rendering rather
 * than when the messages arrive. Pure.
 */
export const withTransactionSenders = (messages, transaction) => {
  const parties = [transaction?.customer, transaction?.provider].filter(p => p?.id);
  if (!parties.length) {
    return messages;
  }
  return (messages || []).map(m => {
    const party = parties.find(p => p.id.uuid === m.sender?.id?.uuid);
    return party && party !== m.sender ? { ...m, sender: party } : m;
  });
};

/** A retry-safe id for one send attempt. */
export const newClientId = () =>
  `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
